import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import dataService from 'services/dataService';
import {
  apiPatientChartSessionEnd,
  apiPatientChartSessionStart,
} from 'services/dataService/resources';
import { UserType } from 'infrastructure/enums';

import { showResult } from '../utils';

const REASONS = {
  navigateAway: 'navigate-away',
  tabClose: 'tab-close',
  background: 'background',
};

const startPatientChartSession = async ({
  patientGuid,
}: {
  patientGuid: string;
}) => {
  const { data, error } = await dataService.createOne(
    apiPatientChartSessionStart(),
    {
      patientGuid,
    },
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

const endPatientChartSession = ({
  sessionId,
  reasonEnded,
}: {
  sessionId: string | null;
  reasonEnded: string;
}) => {
  if (sessionId) {
    const body = {
      sessionId,
      reasonEnded,
    };
    navigator.sendBeacon(apiPatientChartSessionEnd(), JSON.stringify(body));
  }
};

export const usePatientChartSession = ({
  patientGuid,
  userType,
}: {
  patientGuid: string;
  userType: UserType;
}) => {
  const [sessionId, setSessionId] = useState(null);

  const { mutate, isLoading, ...other } = useMutation({
    mutationFn: startPatientChartSession,
    onSuccess(data) {
      setSessionId(data.sessionId);
    },
  });

  const endRef = useRef(false);
  const sessionRef = useRef<string | null>(null);

  useEffect(() => {
    sessionRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    if (!patientGuid || !userType) return;

    const endOnce = (reason: string) => {
      if (endRef.current) return;
      if (!sessionRef.current) return;

      endRef.current = true;

      endPatientChartSession({
        sessionId: sessionRef.current,
        reasonEnded: reason,
      });

      sessionRef.current = null;
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'hidden' &&
        userType === UserType.HomeHealth
      ) {
        endOnce(REASONS.background);
      }

      if (document.visibilityState === 'visible') {
        if (!sessionRef.current && userType === UserType.HomeHealth) {
          endRef.current = false;
          mutate({ patientGuid });
        }
      }
    };

    const handlePageHide = () => {
      endOnce(REASONS.tabClose);
    };

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    handleVisibilityChange();

    return () => {
      endOnce(REASONS.navigateAway);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [patientGuid]);

  return {
    startSession: mutate,
    endSession: endPatientChartSession,
    isLoading,
    sessionId,
    ...other,
  };
};
